<!--
@component
AppLayout

Main app layout — header, menu, footer and analytics wrapping every page.
-->

<script lang="ts">
  import type { Snippet } from 'svelte';
  import getPath from '$utils/getPath';
  import {
    AdScripts,
    LeaderboardAd,
    SiteHeader,
    SiteFooter,
    Theme,
    Article,
    EmbedPreviewerLink,
  } from '@reuters-graphics/graphics-components';
  import ReferralBlock from '$lib/components/common/ReferralBlock/index.svelte';
  import { navigating } from '$app/state';
  import { page } from '$app/state';
  import { browser, dev } from '$app/environment';
  import Spinner from '$lib/components/common/Spinner/index.svelte';
  import Menu from '$lib/components/common/Menu/index.svelte';
  import Endnotes from '$lib/components/common/Endnotes/index.svelte';
  import Analytics from '$lib/components/common/Analytics/Analytics.svelte';
  import { appState } from '$lib/stores/index.svelte';
  import { getTheme } from '$lib/styles/theme';

  // Styles
  import '@reuters-graphics/graphics-components/scss/main.scss';
  import '$lib/styles/global.scss';

  interface Props {
    children: Snippet;
    data: any;
  }

  let { children, data }: Props = $props();

  let content = $derived(data.content);

  $effect(() => {
    if (page.route.id?.split('/').slice(-1)[0] === 'share') {
      appState.embed = true;
    } else {
      appState.embed = false;
    }
  });

  // `appState.showKnockouts` is a `$derived` on the store (see
  // `src/lib/stores/index.svelte.ts`), so it stays in sync with
  // `appState.matches` synchronously — no effect needed and no
  // first-render flash between the Groups table and the Bracket.

  // Checks for Reuters mobile app using search params
  const isReutersMobileApp =
    browser && page.url?.searchParams.get('outputType') === 'chromeless';

  let innerWidth = $state(0);

  $effect(() => {
    appState.windowWidth = innerWidth;
  });

  // Each route layout is responsible for setting `activeCountry`. Here
  // we clear it whenever we leave a route that owns one (currently just
  // the team route) so the menu highlight resets on navigation.
  $effect(() => {
    if (!page.route.id?.includes('/teams/[slug]')) {
      appState.activeCountry = [];
    }
  });

  // Analytics (initial pageview + every SPA navigation, both gated on
  // `isReutersDotcom`) is fully owned by the `<Analytics />` component
  // mounted below. See `src/lib/components/common/Analytics/Analytics.svelte`.
</script>

<svelte:window bind:innerWidth />

<svelte:head>
  <link rel="preload" as="image" href={getPath('images/icons/football.png')} />
  <link
    rel="preload"
    as="image"
    href={getPath('images/flags/rects/UNKNOWN.png')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/flags/circles/UNKNOWN.png')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/icons/cup-icon-euro.png')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/branding/navbar-texture.svg')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/branding/match-card-texture.svg')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/branding/match-card-texture-reverse.svg')}
  />

  {#each appState.teams as team}
    <link
      rel="preload"
      as="image"
      href={getPath(`images/flags/rects/${team.slug}.png`)}
    />
    <link
      rel="preload"
      as="image"
      href={getPath(`images/flags/circles/${team.slug}.png`)}
    />
  {/each}
</svelte:head>

<!--
  Analytics is mounted once, at the app-group layout level. The component
  itself checks the hostname (only fires on Reuters domains), initialises
  gtag.js, registers the initial pageview, and registers subsequent
  pageviews on SPA navigation. Individual page components no longer need
  their own `<Analytics />` element.
-->
<Analytics />

<Theme base="light" theme={getTheme(appState.windowWidth)}>
  <!-- Reuters navbar -->
  {#if !isReutersMobileApp && !appState.embed}
    {#if !dev}
      <AdScripts />
      <!-- <LeaderboardAd /> -->
    {/if}
    <SiteHeader />
  {/if}
  <Article>
    <!-- App menu -->
    {#if !appState.embed}
      <Menu />
    {/if}

    {#if navigating.to}
      <Spinner />
    {:else}
      <div class="content" class:is-loading={appState.isLoading}>
        {@render children()}
      </div>
    {/if}
    <ReferralBlock content={content.referral} />
    <!-- Bylines -->
    {#if !appState.embed}
      <Endnotes notes={content.endNotes} />
    {/if}
  </Article>
  <!-- Reuters footer -->
  {#if !isReutersMobileApp && !appState.embed}
    <SiteFooter />
  {/if}
</Theme>

<!-- Only visible in dev! -->
<EmbedPreviewerLink {dev} />
